import { io, Socket } from 'socket.io-client';
import { Notification } from '../contexts/NotificationContext';

class SocketService {
  private socket: Socket | null = null;

  // Connect to socket server
  connect(authToken: string): void {
    if (this.socket?.connected) {
      return;
    }
    
    this.socket = io(import.meta.env.VITE_SOCKET_URL || 'http://localhost:3001', {
      auth: {
        token: authToken,
      },
      transports: ['websocket', 'polling'],
      timeout: 10000,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    this.setupEventListeners();
  }

  // Disconnect from socket server
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  // Check if socket is connected
  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  // Emit event
  emit(event: string, data?: any): void {
    if (this.socket?.connected) {
      this.socket.emit(event, data);
    } else {
      console.warn('Socket not connected. Cannot emit event:', event);
    }
  }

  // Listen to event
  on(event: string, callback: (data: any) => void): void {
    if (this.socket) {
      this.socket.on(event, callback);
    }
  }

  // Remove event listener
  off(event: string, callback?: (data: any) => void): void {
    if (this.socket) {
      if (callback) {
        this.socket.off(event, callback);
      } else {
        this.socket.off(event);
      }
    }
  }

  // Setup default event listeners
  private setupEventListeners(): void {
    if (!this.socket) return;

    // Connection events
    this.socket.on('connect', () => {
      console.log('Socket connected:', this.socket?.id);
    });

    this.socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
    });

    this.socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
    });

    this.socket.on('reconnect', (attemptNumber) => {
      console.log('Socket reconnected after', attemptNumber, 'attempts');
    });

    this.socket.on('reconnect_error', (error) => {
      console.error('Socket reconnection error:', error);
    });

    this.socket.on('reconnect_failed', () => {
      console.error('Socket reconnection failed');
    });

    // Authentication events
    this.socket.on('auth_error', (error) => {
      console.error('Socket authentication error:', error);
      this.disconnect();
      // Redirect to login or show error
      window.location.href = '/login';
    });
  }

  // Notification-specific methods
  onNotification(callback: (notification: Notification) => void): void {
    this.on('new_notification', callback);
  }

  requestUnreadCount(): void {
    this.emit('get_unread_count');
  }

  markNotificationAsRead(notificationId: string): void {
    this.emit('mark_notification_read', { notificationId });
  }

  removeNotificationListeners(): void {
    this.off('new_notification');
  }

  // Request-specific methods
  onNewRequest(callback: (request: any) => void): void {
    this.on('new_request', callback);
  }

  onRequestUpdate(callback: (request: any) => void): void {
    this.on('request_updated', callback);
  }

  onRequestExpired(callback: (request: any) => void): void {
    this.on('request_expired', callback);
  }

  removeRequestListeners(): void {
    this.off('new_request');
    this.off('request_updated');
    this.off('request_expired');
  }

  // Offer-specific methods
  onNewOffer(callback: (offer: any) => void): void {
    this.on('new_offer', callback);
  }

  onOfferUpdate(callback: (offer: any) => void): void {
    this.on('offer_updated', callback);
  }

  onOfferExpired(callback: (offer: any) => void): void {
    this.on('offer_expired', callback);
  }

  removeOfferListeners(): void {
    this.off('new_offer');
    this.off('offer_updated');
    this.off('offer_expired');
  }

  // Admin-specific methods
  onSellerStatusUpdate(callback: (seller: any) => void): void {
    this.on('seller_status_updated', callback);
  }

  onSystemUpdate(callback: (update: any) => void): void {
    this.on('system_update', callback);
  }

  removeAdminListeners(): void {
    this.off('seller_status_updated');
    this.off('system_update');
  }

  // Join/leave rooms
  joinRoom(room: string): void {
    this.emit('join_room', { room });
  }

  leaveRoom(room: string): void {
    this.emit('leave_room', { room });
  }

  // Typing indicators (for future chat functionality)
  startTyping(room: string): void {
    this.emit('typing_start', { room });
  }

  stopTyping(room: string): void {
    this.emit('typing_stop', { room });
  }

  onTyping(callback: (data: { userId: string; room: string }) => void): void {
    this.on('user_typing', callback);
  }

  onStopTyping(callback: (data: { userId: string; room: string }) => void): void {
    this.on('user_stop_typing', callback);
  }

  removeTypingListeners(): void {
    this.off('user_typing');
    this.off('user_stop_typing');
  }

  // Chat-specific methods
  onChatMessage(callback: (data: { offerId: string; message: any }) => void): void {
    this.on('chat_message', callback);
  }

  onChatTyping(callback: (data: { offerId: string; userId: string }) => void): void {
    this.on('chat_typing', callback);
  }

  emitChatTyping(offerId: string, targetUserId: string): void {
    this.emit('chat_typing', { offerId, targetUserId });
  }

  removeChatListeners(): void {
    this.off('chat_message');
    this.off('chat_typing');
  }

  // Cleanup all listeners
  removeAllListeners(): void {
    this.removeNotificationListeners();
    this.removeRequestListeners();
    this.removeOfferListeners();
    this.removeAdminListeners();
    this.removeTypingListeners();
    this.removeChatListeners();
  }

  // Get socket instance (for advanced usage)
  getSocket(): Socket | null {
    return this.socket;
  }

  // Get connection status
  getConnectionStatus(): {
    connected: boolean;
    id?: string;
    transport?: string;
  } {
    if (!this.socket) {
      return { connected: false };
    }

    return {
      connected: this.socket.connected,
      id: this.socket.id,
      transport: this.socket.io.engine?.transport?.name,
    };
  }
}

// Create and export singleton instance
export const socketService = new SocketService();
export default socketService;